import { bindActionCreators } from "@reduxjs/toolkit";
import { type FormikProps, withFormik } from "formik";
import { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { ValidatedForm } from "../../../../../components";
import ExpandableLinks from "../../../../../components/ExpandableItems/ExpandableLinks/ExpandableLinks";
import { bindAction, type WizardForm } from "../../../../../interfaces";
import { validationSchemas, formikUtils, type AutosaveProps, withAutosave } from "../../../../../utils";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { type TagsPayloadCamelPlural } from "../../../../../interfaces/Tags";
import * as messageEntityStateActions from "../state/actions/messageEntityStateActions";
import { reset, saveMessage } from "../state/slices/messageDetailsSlice";
import { type MessageDetails } from "../types";

import styles from "./MessageInfoForm.module.scss";

type OwnProps = {
  normalizedTags?: TagsPayloadCamelPlural;
  isReadOnlyView: boolean;
};

export type Props = OwnProps &
  FormikProps<MessageDetails> &
  AutosaveProps &
  PropsFromRedux &
  WizardForm<MessageDetails>;

const utmCampaignField = "utmCampaign";

const MessageInfoForm: React.FC<Props> = (props) => {
  const {
    values,
    isLoading,
    dirty,
    acceptHandlers,
    entityStateActions,
    clearInfo,
    id,
    saveMessage,
    onIsValidChange,
    onIsDirtyChanged,
    isValid,
    onBlur,
    disabled,
    isReadOnlyView,
    flows,
  } = props;
  const validatedFieldProps = formikUtils.getValidateFieldProps(props);

  const save = () => {
    saveMessage(values);
    if (id <= 0) {
      entityStateActions.create({ title: values.title, utmCampaign: values.utmCampaign });
    }
  };

  const onClose = () => {
    entityStateActions.resetMessageEntityState();
    clearInfo();
  };

  useEffect(() => {
    acceptHandlers?.({
      onNext: save,
      onClose,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    onIsValidChange?.(isValid);
  }, [isValid, onIsValidChange]);

  useEffect(() => {
    onIsDirtyChanged?.(dirty);
  }, [dirty, onIsDirtyChanged]);

  const onTitleBlur = (propertyName: string) => {
    const { values, setFieldValue, onBlur, touched } = props;
    if (values.id <= 0 && !touched[utmCampaignField]) {
      setFieldValue(utmCampaignField, values.title, true);
    }

    onBlur(propertyName);
  };

  return (
    <ValidatedForm
      disablePreventTransitionPrompt={id > 0}
      unsavedChangesPrompt={{
        title: "Exit Without Saving?",
        message: "Are you sure you want to exit without saving this message? All information entered will be lost.",
      }}
      dirty={dirty}
      isLoading={isLoading}
      isSubmitting={isLoading}
      formWidthComputer={8}
    >
      <div className={styles.root}>
        <ValidatedForm.InputField
          {...validatedFieldProps}
          label="Title"
          value={values.title}
          propertyName="title"
          markAsRequired
          placeholder="Enter Title"
          onBlur={onTitleBlur}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.InputField
          {...validatedFieldProps}
          label="Campaign Name"
          value={values.utmCampaign}
          propertyName="utmCampaign"
          markAsRequired
          placeholder="Campaign Name"
          onBlur={onBlur}
          disabled={disabled}
          tooltip={{
            info: "Sets the campaign name for tracking in analytics. Defaults to your email title but can be customized.",
            width: 20,
          }}
          isReadOnlyView={isReadOnlyView}
        />
        <ExpandableLinks
          label="Associated Flows"
          linkFormatter={(linkId) => `/content/flows/${linkId}/items`}
          iconName="fa-code-branch"
          links={flows}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState="No associated Flows"
        />
      </div>
    </ValidatedForm>
  );
};

const mapToMessagePayload = (values: MessageDetails): MessageDetails => {
  return {
    id: values.id,
    title: values.title.trim(),
    utmCampaign: values.utmCampaign.trim(),
  };
};

const mapState = (state: RootState, ownProps: any) => ({
  message: state.library.messages.details.values.message,
  id: ownProps.entityId || state.library.messages.details.messageEntityStateReducer.entityId,
  isLoading: state.library.emails.emailDetailsReducer.isLoading,
  isLoaded: state.library.emails.emailDetailsReducer.isEmailLoaded,
  isDraft: state.library.emails.emailDetailsReducer.email.isDraft,
  isCreating: state.library.emails.emailEntityStateReducer.changingEntityState,
  hasBeenPublished: state.library.emails.emailDetailsReducer.email.hasBeenPublished,
  flows: state.library.messages.details.values.flows,
});

const mapDispatch = (dispatch: AppDispatch) => ({
  entityStateActions: bindActionCreators(messageEntityStateActions, dispatch),
  saveMessage: bindAction(saveMessage, dispatch),
  clearInfo: bindAction(reset, dispatch),
});

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

const MessageInfoWithAutosave = withAutosave<Props, MessageDetails>({
  getInitValues: (props) => mapToMessagePayload(props.message),
  stateProvider: mapToMessagePayload,
  entityUpdater: (props) => props.entityStateActions.updateMessage,
  isValid: (props) => props.id && props.isValid && props.dirty,
})(MessageInfoForm);

const MessageInfoWithFormik = withFormik({
  mapPropsToValues: (props: OwnProps & PropsFromRedux) => props.message,
  handleSubmit: () => {},
  enableReinitialize: true,
  validationSchema: validationSchemas.messageInfo,
  validateOnMount: true,
})(MessageInfoWithAutosave);

const ConnectedComponent = connector(MessageInfoWithFormik);
export default ConnectedComponent;
